import { useState } from 'react'
import { type MenuOption } from '@opallabs/concepts'
import { Theme } from '@opallabs/gem'
import {
  Radio,
  ParagraphText,
  StrongText,
  SingleSelect,
} from '@opallabs/lapidary'
import * as styles from './styles'
import { Title } from './Title'

export const contentUpdatingDescription =
  'Keep your presentation automatically up-to-date with the latest changes.'
export const contentDisplayDescription =
  'Toggles additional functionality on content like Chat, Notes, Options and more.'
export const autoCreateSlidesDescription =
  "Automatically create slides for new moments that match this presentation's filters."

type EditorSidebarProps = {
  contentAdditions?: number
  contentDisplayDefaultValue: boolean
  contentUpdatingDefaultValue: boolean
  autoCreateSlidesDefaultValue: boolean
  onReviewContent: () => void
  setContentDisplay: (value: boolean) => void
  setContentUpdating: (value: boolean) => void
  setAutoCreateSlides: (value: boolean) => void
  setTitle: (value: string) => void
  themes: MenuOption[]
  selectedThemeId: string | null
  onThemeChange: (themeId: string) => void
  theme: Theme
  title: string
}

export const PresentationEditorSidebar: React.VFC<EditorSidebarProps> = ({
  contentDisplayDefaultValue,
  contentUpdatingDefaultValue,
  autoCreateSlidesDefaultValue,
  setContentDisplay,
  setContentUpdating,
  setAutoCreateSlides,
  setTitle,
  themes = [],
  selectedThemeId,
  onThemeChange,
  theme = 'light',
  title,
}) => {
  const defaultOption: MenuOption = {
    value: 'default',
    label: 'Default',
  }
  const themeOptions: Array<MenuOption> = [
    defaultOption,
    ...themes.map((t) => ({ value: t.value, label: t.label })),
  ]

  const propsThemeOption =
    themeOptions.find((o) => o.value === selectedThemeId) ?? defaultOption

  const [localOverride, setLocalOverride] = useState<MenuOption | null>(null)

  const currentTheme = localOverride ?? propsThemeOption

  const handleThemeChange = (option: MenuOption | null): void => {
    if (!option) return
    setLocalOverride(option)
    onThemeChange(option.value as string)
  }

  return (
    <div css={styles.container}>
      <div css={styles.field(theme)}>
        <Title title={title} onChange={setTitle} theme={theme} />
      </div>

      {themeOptions.length > 1 && (
        <div css={styles.field(theme)}>
          <ParagraphText theme={theme}>
            <StrongText styleOverrides={styles.title}>Theme</StrongText>
          </ParagraphText>
          <SingleSelect.ControlledRoot
            options={themeOptions}
            selectedOption={currentTheme}
            onChange={handleThemeChange}
          >
            <SingleSelect.Body>
              <SingleSelect.Trigger>
                <SingleSelect.TriggerText />
              </SingleSelect.Trigger>
              <SingleSelect.Menu>
                <SingleSelect.MenuItems />
              </SingleSelect.Menu>
            </SingleSelect.Body>
          </SingleSelect.ControlledRoot>
        </div>
      )}

      <div css={styles.field(theme)}>
        <ParagraphText theme={theme}>
          <StrongText styleOverrides={styles.title}>
            Automatic Content Updating
          </StrongText>
        </ParagraphText>
        <ParagraphText color="Gray" theme={theme}>
          {contentUpdatingDescription}
        </ParagraphText>
        <Radio.Group
          name="content-updating"
          defaultValue={contentUpdatingDefaultValue.toString()}
          onChange={(e) => {
            const { value } = e.target
            setContentUpdating(value === 'true')
          }}
          theme={theme}
        >
          <Radio.Input label="Update" value="true" />
          <Radio.Input label="Don’t Update" value="false" />
        </Radio.Group>
      </div>

      <div css={styles.field(theme)}>
        <ParagraphText theme={theme}>
          <StrongText styleOverrides={styles.title}>
            Automatic Slide Creation
          </StrongText>
        </ParagraphText>
        <ParagraphText color="Gray" theme={theme}>
          {autoCreateSlidesDescription}
        </ParagraphText>
        <Radio.Group
          name="auto-create-slides"
          defaultValue={autoCreateSlidesDefaultValue.toString()}
          onChange={(e) => {
            const { value } = e.target
            setAutoCreateSlides(value === 'true')
          }}
          theme={theme}
        >
          <Radio.Input label="Automatic creation" value="true" />
          <Radio.Input label="Manual creation" value="false" />
        </Radio.Group>
      </div>

      <div css={styles.field(theme)}>
        <ParagraphText theme={theme}>
          <StrongText styleOverrides={styles.title}>Content Display</StrongText>
        </ParagraphText>
        <ParagraphText color="Gray" theme={theme}>
          {contentDisplayDescription}
        </ParagraphText>
        <Radio.Group
          name="content-display"
          defaultValue={contentDisplayDefaultValue.toString()}
          onChange={(e) => {
            const { value } = e.target
            setContentDisplay(value === 'true')
          }}
          theme={theme}
        >
          <Radio.Input label="Show content controls" value="true" />
          <Radio.Input label="Hide content controls" value="false" />
        </Radio.Group>
      </div>
    </div>
  )
}
